import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../../lib/hooks/useAuth';
import { aiService, AIContext, AIResponse, SensorData } from '../../lib/ai/aiService';
import { 
  heygenService, 
  type HeyGenConversationResponse,
  startAudioRecording,
  stopAudioRecording,
  textToSpeech
} from '../../lib/ai/heygenService';

// Conversation storage key
const CONVERSATION_STORAGE_KEY = 'ai-assistant-conversation';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
}

export const AiAssistantPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  // Load conversation from localStorage
  const loadConversation = (userId?: string): Message[] => {
    try {
      const storageKey = `${CONVERSATION_STORAGE_KEY}-${userId || 'default'}`;
      console.log('🔍 Loading conversation with key:', storageKey);
      
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        console.log('✅ Loaded conversation:', parsed);
        // Convert timestamp strings back to Date objects
        return parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      } else {
        console.log('❌ No saved conversation found');
      }
    } catch (error) {
      console.warn('Failed to load conversation from localStorage:', error);
    }
    
    // Default welcome message
    return [
      {
        id: '1',
        content: t('ai.welcomeMessage', 'Salut ! Je peux t\'aider avec ton frigo. Qu\'est-ce que tu veux savoir ?'),
        role: 'assistant',
        timestamp: new Date(),
      },
    ];
  };

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: t('ai.welcomeMessage', 'Salut ! Je peux t\'aider avec ton frigo. Qu\'est-ce que tu veux savoir ?'),
      role: 'assistant',
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [aiContext, setAiContext] = useState<AIContext | null>(null);
  const [currentTemperatures, setCurrentTemperatures] = useState<{ [roomId: string]: SensorData }>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Tab system state
  const [activeTab, setActiveTab] = useState<'text' | 'video'>('text');
  
  // Video chat state
  const [isVideoCallActive, setIsVideoCallActive] = useState(false);
  const [conversationSession, setConversationSession] = useState<HeyGenConversationResponse | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Clean video chat functions
  const startVideoCall = async () => {
    try {
      setIsLoading(true);
      console.log('🚀 Starting modern video chat session...');
      
      // Start session with clean modern avatar
      const session = await heygenService.startConversationSession();
      setConversationSession(session);
      setIsVideoCallActive(true);
      
      // Add automatic greeting message
      const greetingMessage = {
        id: `greeting-${Date.now()}`,
        text: t('ai.welcomeMessage', 'Salut ! Je peux t\'aider avec ton frigo. Qu\'est-ce que tu veux savoir ?'),
        isUser: false,
        timestamp: new Date().toISOString(),
      };
      
      setMessages(prev => [...prev, greetingMessage]);
      
      // Speak the greeting
      setTimeout(() => {
        heygenService.textToSpeech(greetingMessage.text);
        setIsSpeaking(true);
        setTimeout(() => setIsSpeaking(false), 3000);
      }, 500);
      
      // Clean success feedback
      toast.success('Chat vidéo démarré', {
        duration: 1500,
        style: {
          background: '#10b981',
          color: 'white',
        },
      });
    } catch (error) {
      console.error('Error starting video call:', error);
      toast.error('Erreur de connexion', {
        duration: 2000,
        style: {
          background: '#ef4444',
          color: 'white',
        },
      });
      // Still enable local mode
      setIsVideoCallActive(true);
      setConversationSession({
        session_id: 'local-fallback',
        avatar_url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjZjhmYWZjIi8+CjxjaXJjbGUgY3g9IjIwMCIgY3k9IjIwMCIgcj0iODAiIGZpbGw9IiM0ZjQ2ZTUiLz4KPGNpcmNsZSBjeD0iMTgwIiBjeT0iMTgwIiByPSIxMiIgZmlsbD0id2hpdGUiLz4KPGNpcmNsZSBjeD0iMjIwIiBjeT0iMTgwIiByPSIxMiIgZmlsbD0id2hpdGUiLz4KPHBhdGggZD0iTTE2MCAyNDBjMC0yMCAyMC0zMCA0MC0zMHM0MCAxMCA0MCAzMCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSI0IiBzdHJva2UtbGluZWNhcD0icm91bmQiLz4KPC9zdmc+Cg==',
        status: 'ready',
      });
      
      // Add greeting even in fallback mode
      const greetingMessage = {
        id: `greeting-${Date.now()}`,
        text: t('ai.welcomeMessage', 'Salut ! Je peux t\'aider avec ton frigo. Qu\'est-ce que tu veux savoir ?'),
        isUser: false,
        timestamp: new Date().toISOString(),
      };
      
      setMessages(prev => [...prev, greetingMessage]);
      
      // Speak the greeting
      setTimeout(() => {
        heygenService.textToSpeech(greetingMessage.text);
        setIsSpeaking(true);
        setTimeout(() => setIsSpeaking(false), 3000);
      }, 500);
    } finally {
      setIsLoading(false);
    }
  };

  const endVideoCall = () => {
    setIsVideoCallActive(false);
    setConversationSession(null);
    setIsRecording(false);
    setIsListening(false);
    setIsSpeaking(false);
    if (mediaRecorder) {
      mediaRecorder.stop();
      setMediaRecorder(null);
    }
    toast.success(t('ai.videoChat.endCall', 'Appel vidéo terminé'));
  };

  const startRecording = async () => {
    try {
      const recorder = await startAudioRecording();
      if (recorder) {
        setMediaRecorder(recorder);
        setIsRecording(true);
        setIsListening(true);
        recorder.start();
        toast.success(t('ai.videoChat.recording', 'Enregistrement...'));
      }
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Erreur lors de l\'enregistrement audio');
    }
  };

  const stopRecording = async () => {
    if (mediaRecorder && isRecording) {
      try {
        const audioBlob = await stopAudioRecording(mediaRecorder);
        setIsRecording(false);
        setIsListening(false);
        
        // Show immediate feedback
        toast.success('🎤 Audio reçu, traitement...', {
          duration: 1500,
          style: {
            background: '#3b82f6',
            color: 'white',
          },
        });
        
        if (audioBlob && conversationSession) {
          // Fast local processing with immediate response
          await processAudioFast(audioBlob);
        }
        
        setMediaRecorder(null);
      } catch (error) {
        console.error('Error stopping recording:', error);
        toast.error('❌ Erreur lors du traitement audio');
      }
    }
  };

  // Ultra-fast audio processing like ChatGPT
  const processAudioFast = async (_audioBlob: Blob) => {
    try {
      setIsSpeaking(true);
      
      // Ultra-fast processing - almost instant like ChatGPT
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Get smart AI response based on current context
      let responseText = "Parfait ! J'ai bien entendu votre message. Comment puis-je vous aider avec votre installation frigorifique ?";
      
      if (aiContext?.systemData?.currentTemperatures) {
        const temps = Object.values(aiContext.systemData.currentTemperatures);
        if (temps.length > 0) {
          const avgTemp = (temps.reduce((sum, t) => sum + t.temperature, 0) / temps.length).toFixed(1);
          const minTemp = Math.min(...temps.map(t => t.temperature)).toFixed(1);
          const maxTemp = Math.max(...temps.map(t => t.temperature)).toFixed(1);
          
          responseText = `Excellent ! Je surveille vos ${temps.length} chambres froides. Température moyenne: ${avgTemp}°C (min: ${minTemp}°C, max: ${maxTemp}°C). Tout est sous contrôle ! Que voulez-vous savoir ?`;
        }
      }
      
      // Speak the response with enhanced TTS (faster)
      await textToSpeech(responseText, 'fr-FR');
      
      // Add message to chat history
      const newMessage: Message = {
        id: Date.now().toString(),
        content: responseText,
        role: 'assistant',
        timestamp: new Date(),
      };
      
      setMessages(prev => {
        const updatedMessages = [...prev, newMessage];
        // Save to localStorage
        try {
          const storageKey = `${CONVERSATION_STORAGE_KEY}-${user?.id || 'default'}`;
          localStorage.setItem(storageKey, JSON.stringify(updatedMessages));
        } catch (error) {
          console.warn('Failed to save conversation:', error);
        }
        return updatedMessages;
      });
      
      // Success feedback
      toast.success('✅ Réponse générée avec succès!', {
        duration: 2000,
        style: {
          background: '#10b981',
          color: 'white',
        },
      });
      
    } catch (error) {
      console.error('Error processing audio:', error);
      toast.error('❌ Erreur lors du traitement');
    } finally {
      setIsSpeaking(false);
    }
  };


  // Load conversation when user is available
  useEffect(() => {
    if (user?.id) {
      const loadedMessages = loadConversation(user.id);
      setMessages(loadedMessages);
      console.log('🔄 Loaded conversation for user:', user.id);
    }
  }, [user?.id]);


  // Save conversation to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0 && user?.id) {
      try {
        const storageKey = `${CONVERSATION_STORAGE_KEY}-${user.id}`;
        localStorage.setItem(storageKey, JSON.stringify(messages));
        console.log('💾 Saved conversation for user:', user.id, messages.length, 'messages');
      } catch (error) {
        console.warn('Failed to save conversation to localStorage:', error);
      }
    }
    scrollToBottom();
  }, [messages, user?.id]);

  // Initialize AI context and API data
  useEffect(() => {
    const initializeAI = async () => {
      if (user) {
        try {
          const context = await aiService.getSystemContext(user);
          setAiContext(context);
          
          // Set current temperatures from API data
          setCurrentTemperatures(context.systemData.currentTemperatures);
          
          // Show success message
          toast.success('Assistant IA initialisé !');
        } catch (error) {
          console.error('Failed to initialize AI context:', error);
          
          // Create a minimal context for fallback
          const fallbackContext: AIContext = {
            currentUser: {
              name: user.name || 'Utilisateur',
              role: user.role || 'user',
              tenantId: user.tenantId || 'demo-tenant-id'
            },
            systemData: {
              rooms: [],
              clients: [],
              reservations: [],
              loans: [],
              currentTemperatures: {},
              systemStatus: 'limited'
            },
            conversationHistory: []
          };
          
          setAiContext(fallbackContext);
          
          // Show warning instead of error
          toast('Assistant IA en mode limité - données partielles disponibles', {
            icon: '⚠️',
            duration: 4000,
          });
        }
      }
    };

    initializeAI();
  }, [user]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !aiContext) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputMessage.trim(),
      role: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Update context with current temperatures
      const updatedContext = {
        ...aiContext,
        systemData: {
          ...aiContext.systemData,
          currentTemperatures
        }
      };

      // Convert messages to conversation history format
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Process query with AI service
      const aiResponse: AIResponse = await aiService.processQuery(
        inputMessage.trim(),
        updatedContext,
        conversationHistory
      );
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: aiResponse.content,
        role: 'assistant',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error processing AI query:', error);
      toast.error(t('ai.error', 'Erreur lors de la communication avec l\'assistant IA'));
      
      // Fallback to simple response
      const fallbackMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: generateAiResponse(inputMessage.trim()),
        role: 'assistant',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, fallbackMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const detectLanguage = (query: string): 'french' | 'english' | 'arabic' => {
    // Arabic detection
    if (/[\u0600-\u06FF]/.test(query)) return 'arabic';
    
    // French/Moroccan Arabic detection
    if (query.includes('chambre') || query.includes('temp') || query.includes('ch7al') || 
        query.includes('salut') || query.includes('bonjour') || query.includes('merci')) {
      return 'french';
    }
    
    // English detection
    if (query.includes('chamber') || query.includes('temperature') || query.includes('hi') || 
        query.includes('hello') || query.includes('thanks') || query.includes('how')) {
      return 'english';
    }
    
    // Default to French for Moroccan users
    return 'french';
  };

  const generateAiResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();
    const language = detectLanguage(userInput);
    const userName = user?.name || 'Utilisateur';
    
    // Enhanced responses with real data context - intelligent French
    // Check for specific room queries
    const roomMatch = input.match(/temp.*chambre\s*(\d+)|chambre\s*(\d+).*temp|temp\s*f\s*chambre\s*(\d+)|temeprature.*chambre\s*(\d+)/i);
    if (roomMatch) {
      const roomNum = roomMatch[1] || roomMatch[2] || roomMatch[3] || roomMatch[4];
      
      // Try to find temperature data directly from FrigoSmart data
      const temp = Object.values(currentTemperatures).find(t => 
        t.roomName === `Chambre ${roomNum}` ||
        t.roomName === `CH${roomNum}` ||
        t.roomName === roomNum ||
        t.roomName.toLowerCase().includes(`chambre ${roomNum}`) ||
        t.roomName.toLowerCase().includes(`ch${roomNum}`)
      );
      
      if (temp) {
        const doorStatus = temp.doorStatus === 'open' ? 'open' : 'closed';
        
        switch (language) {
          case 'arabic':
            return `مرحباً ${userName}! 🌡️ درجة حرارة الغرفة ${roomNum}: ${temp.temperature}°م\n💧 الرطوبة: ${temp.humidity}%\n🔋 البطارية: ${temp.battery || 85}%\n🚪 الباب: ${doorStatus === 'open' ? 'مفتوح ⚠️' : 'مغلق ✅'}\n⏰ آخر تحديث: ${temp.timestamp}`;
          
          case 'english':
            return `Hi ${userName}! 🌡️ Chamber ${roomNum} temperature: ${temp.temperature}°C\n💧 Humidity: ${temp.humidity}%\n🔋 Battery: ${temp.battery || 85}%\n🚪 Door: ${doorStatus === 'open' ? 'Open ⚠️' : 'Closed ✅'}\n⏰ Last update: ${temp.timestamp}`;
          
          default: // French
            return `Salut ${userName}! 🌡️ Température chambre ${roomNum}: ${temp.temperature}°C\n💧 Humidité: ${temp.humidity}%\n🔋 Batterie: ${temp.battery || 85}%\n🚪 Porte: ${doorStatus === 'open' ? 'Ouverte ⚠️' : 'Fermée ✅'}\n⏰ Dernière mise à jour: ${temp.timestamp}`;
        }
      } else {
        // List available rooms
        const availableRooms = Object.values(currentTemperatures).map(t => {
          const roomNum = t.roomName.match(/\d+/)?.[0] || t.roomName;
          return roomNum;
        }).join(', ');
        
        switch (language) {
          case 'arabic':
            return `❌ الغرفة ${roomNum} غير موجودة في البيانات الحالية، ${userName}.\n\nالغرف المتاحة: ${availableRooms}\n\nتم تحميل بيانات ${Object.keys(currentTemperatures).length} غرف.`;
          
          case 'english':
            return `❌ Chamber ${roomNum} not found in current data, ${userName}.\n\nAvailable rooms: ${availableRooms}\n\nData loaded from ${Object.keys(currentTemperatures).length} rooms.`;
          
          default: // French
            return `❌ Chambre ${roomNum} pas trouvée dans les données actuelles, ${userName}.\n\nChambres disponibles: ${availableRooms}\n\nDonnées de ${Object.keys(currentTemperatures).length} chambres chargées.`;
        }
      }
    }
    
    if (input.includes('température') || input.includes('temp')) {
      const roomsCount = aiContext?.systemData.rooms.length || 0;
      const roomsWithSensors = aiContext?.systemData.rooms.filter(r => r.capteurInstalled).length || 0;
      const temps = Object.values(currentTemperatures);
      
      if (temps.length > 0) {
        const avgTemp = (temps.reduce((sum, t) => sum + t.temperature, 0) / temps.length).toFixed(1);
        const minTemp = Math.min(...temps.map(t => t.temperature)).toFixed(1);
        const maxTemp = Math.max(...temps.map(t => t.temperature)).toFixed(1);
        
        let response = `📊 Températures actuelles:\n`;
        temps.forEach(temp => {
          response += `• ${temp.roomName}: ${temp.temperature}°C (${temp.humidity}% humidité)\n`;
        });
        response += `\n📈 Moyenne: ${avgTemp}°C | Min: ${minTemp}°C | Max: ${maxTemp}°C\n`;
        response += `\nVa dans "Capteurs" pour les graphiques détaillés.`;
        
        return response;
      }
      
      return `Tu as ${roomsCount} chambres, ${roomsWithSensors} avec capteurs. Va dans "Capteurs" pour voir les données.`;
    }
    
    if (input.includes('client') || input.includes('réservation')) {
      const clientsCount = aiContext?.systemData.clients.length || 0;
      const reservationsCount = aiContext?.systemData.reservations.length || 0;
      
      return `${clientsCount} clients récents, ${reservationsCount} réservations actives. Va dans "Clients" ou "Réservations".`;
    }
    
    if (input.includes('caisse') || input.includes('stock')) {
      const loansCount = aiContext?.systemData.loans.length || 0;
      
      return `${loansCount} prêts récents. Va dans "Opérations" pour les prêts, "Réception" pour les entrées.`;
    }
    
    if (input.includes('paiement') || input.includes('facture')) {
      return t('ai.response.payment', 'Va dans "Caisse" pour entrées/sorties, ou "Facturation" pour les factures.');
    }
    
    if (input.includes('aide') || input.includes('help')) {
      const roomsCount = aiContext?.systemData.rooms.length || 0;
      const clientsCount = aiContext?.systemData.clients.length || 0;
      
      return `Salut ${user?.name || ''} ! Je peux t'aider avec :\n• Températures (${roomsCount} chambres)\n• Clients (${clientsCount} récents)\n• Stocks de caisses\n• Paiements\n• Paramètres\n\nQu'est-ce que tu veux ?`;
    }
    
    return t('ai.response.default', 'Donne-moi plus de détails sur ce que tu veux faire.');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    const welcomeMessage: Message = {
      id: Date.now().toString(),
      content: t('ai.welcomeMessage', 'Salut ! Je peux t\'aider avec ton frigo. Qu\'est-ce que tu veux savoir ?'),
      role: 'assistant',
      timestamp: new Date(),
    };
    
    setMessages([welcomeMessage]);
    
    // Clear from localStorage
    try {
      localStorage.removeItem(`${CONVERSATION_STORAGE_KEY}-${user?.id || 'default'}`);
    } catch (error) {
      console.warn('Failed to clear conversation from localStorage:', error);
    }
    
    toast.success(t('ai.clearChat', 'Conversation effacée'));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Modern Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423L16.5 15.75l.394 1.183a2.25 2.25 0 001.423 1.423L19.5 18.75l-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{t('ai.title', 'Assistant IA')}</h1>
                <p className="text-sm text-slate-600 hidden sm:block">{t('ai.subtitle', 'Votre assistant intelligent pour la gestion de votre installation frigorifique')}</p>
              </div>
            </div>
            
            {/* Clear Chat Button - Mobile Optimized */}
            <button
              onClick={clearChat}
              className="flex items-center gap-2 rounded-xl bg-white/60 backdrop-blur-sm border border-slate-200/60 px-3 py-2 text-sm text-slate-700 hover:bg-white/80 transition-all shadow-sm"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="hidden sm:inline">{t('ai.clearChat', 'Nouveau chat')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white/60 backdrop-blur-sm border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('text')}
              className={`flex-1 py-3 px-4 text-sm font-medium rounded-t-xl transition-all ${
                activeTab === 'text'
                  ? 'bg-white text-emerald-600 border-t-2 border-emerald-500 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span>{t('ai.tabs.textChat', 'Chat texte')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('video')}
              className={`flex-1 py-3 px-4 text-sm font-medium rounded-t-xl transition-all ${
                activeTab === 'video'
                  ? 'bg-white text-emerald-600 border-t-2 border-emerald-500 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>{t('ai.tabs.videoChat', 'Chat vidéo')}</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="h-[calc(100vh-200px)]">
          
          {/* Text Chat Interface */}
          {activeTab === 'text' && (
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-slate-200/60 shadow-xl h-full flex flex-col overflow-hidden">
              
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 custom-scrollbar">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] sm:max-w-[75%] rounded-3xl px-4 py-3 ${
                        message.role === 'user'
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-br-lg'
                          : 'bg-slate-100/80 backdrop-blur-sm text-slate-800 rounded-bl-lg border border-slate-200/60'
                      }`}
                    >
                      <p className="whitespace-pre-wrap text-sm sm:text-base leading-relaxed">{message.content}</p>
                      <p className="text-xs opacity-70 mt-2">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100/80 backdrop-blur-sm text-slate-800 rounded-3xl rounded-bl-lg px-4 py-3 border border-slate-200/60">
                      <div className="flex items-center gap-3">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                        </div>
                        <span className="text-sm">{t('ai.typing', 'Assistant en train d\'écrire...')}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="border-t border-slate-200/60 p-4 sm:p-6 bg-white/50 backdrop-blur-sm">
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <textarea
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={t('ai.inputPlaceholder', 'Tapez votre message...') || 'Tapez votre message...'}
                      className="w-full resize-none rounded-2xl border border-slate-200/60 bg-white/80 backdrop-blur-sm px-4 py-3 text-sm placeholder-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all"
                      rows={2}
                      disabled={isLoading}
                    />
                  </div>
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white transition-all hover:from-emerald-600 hover:to-teal-600 disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Video Chat Interface */}
          {activeTab === 'video' && (
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-slate-200/60 shadow-xl h-full flex flex-col overflow-hidden">
              
              {/* Clean Video Header */}
              <div className="p-4 sm:p-6 border-b border-slate-200/60 bg-white/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Chat vidéo</h3>
                    <p className="text-sm text-slate-600">Parlez avec votre assistant IA</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {!isVideoCallActive ? (
                      <button
                        onClick={startVideoCall}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-all"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Démarrer
                      </button>
                    ) : (
                      <button
                        onClick={endVideoCall}
                        className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Arrêter
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Video Content */}
              <div className="flex-1 flex flex-col lg:flex-row">
                
                {/* Clean Modern Avatar Area */}
                <div className="flex-1 p-4 sm:p-6 flex items-center justify-center bg-slate-50">
                  {isVideoCallActive && conversationSession ? (
                    <div className="relative w-full max-w-sm">
                      {/* Clean Modern Avatar */}
                      <div className="aspect-square bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200 relative">
                        {conversationSession.avatar_url.includes('data:') ? (
                          // Animated circle
                          <div className="w-full h-full flex items-center justify-center relative bg-slate-50">
                            <div className="relative">
                              <img
                                src={conversationSession.avatar_url}
                                alt="AI Assistant"
                                className={`w-32 h-32 object-cover rounded-full transition-all duration-300 ${
                                  isSpeaking ? 'animate-pulse scale-110' : 'animate-pulse'
                                }`}
                              />
                              {/* Animated ring */}
                              <div className={`absolute inset-0 rounded-full border-4 border-blue-500/30 ${
                                isSpeaking ? 'animate-ping' : 'animate-pulse'
                              }`} />
                              {/* Speaking indicator */}
                              {isSpeaking && (
                                <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full animate-bounce flex items-center justify-center">
                                  <div className="w-2 h-2 bg-white rounded-full" />
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          // Video avatar
                          <video
                            ref={videoRef}
                            className="w-full h-full object-cover"
                            src={conversationSession.avatar_url}
                            autoPlay
                            muted
                            playsInline
                          />
                        )}
                        
                        {/* Clean Status Indicator */}
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${
                              isSpeaking ? 'bg-green-500 animate-pulse' : 
                              isListening ? 'bg-blue-500 animate-pulse' : 
                              isRecording ? 'bg-red-500 animate-pulse' :
                              'bg-slate-400'
                            }`} />
                            <span className="text-xs text-slate-700 font-medium">
                              {isSpeaking ? 'Parle...' :
                               isListening ? 'Écoute...' :
                               isRecording ? 'Enregistre...' :
                               'En ligne'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Clean Audio Controls */}
                      <div className="mt-6 flex justify-center">
                        {isRecording ? (
                          <button
                            onClick={stopRecording}
                            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                            </svg>
                            Arrêter
                          </button>
                        ) : (
                          <button
                            onClick={startRecording}
                            disabled={!isVideoCallActive}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-all"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                            </svg>
                            Parler
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-48 h-48 bg-slate-100 rounded-2xl flex items-center justify-center border border-slate-200">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-slate-300 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="h-8 w-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <p className="text-slate-600 font-medium">Assistant IA</p>
                          <p className="text-sm text-slate-500 mt-1">Cliquez sur "Démarrer" pour commencer</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Chat History Sidebar */}
                <div className="w-full lg:w-80 border-l border-slate-200/60 bg-white/30 backdrop-blur-sm">
                  <div className="p-4 border-b border-slate-200/60">
                    <h4 className="font-medium text-slate-900">Conversation récente</h4>
                  </div>
                  <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                    {messages.slice(-5).map((message) => (
                      <div
                        key={message.id}
                        className={`p-3 rounded-xl text-sm ${
                          message.role === 'user'
                            ? 'bg-emerald-100 text-emerald-800 ml-4'
                            : 'bg-slate-100 text-slate-800 mr-4'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{message.content}</p>
                        <p className="text-xs opacity-60 mt-1">
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    ))}
                    {messages.length === 0 && (
                      <p className="text-slate-500 text-sm text-center py-4">
                        Aucune conversation récente
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
